import { FC } from 'react';
import MessageContainer from './MessageContainer';
import Bubble from '@cps/Bubbles';
import UserMessage from '@cps/MessageBox/UserMessage';
import useMessageStore, { EMessageStatus, EContentType, IMessage } from '@/store/messageStore';
import { EFaqKnows, useChat, useFaq, useMessageHistory } from '@/service/useChat';
import { clone, formatTime, getLocalTime } from '@/utils';
import { isToday } from '@/utils/timeTools';
import { $POST } from '@/service/request';
import { TipsWrap } from '@/assets/styles/common';
import { useCreateChat, useTimeoutConfig } from '@/service/useChatConfig';
import RobotSortOne from '../MessageBox/Robot/RobotSortOne';
import RobotSortTwo from '../MessageBox/Robot/RobotSortTwo';
import RobotSortAnswer from '../MessageBox/Robot/RobotSortAnswer';
import { customerName, robotName } from '@/utils/const';
import { useUserMessage } from '@/store/messageStore';
import useAppStore from '@/store/appStore';
import GetHistoryButton from '../Buttons/GetHistoryButton';
import useUserStore from '@/store/userStore';
import CustomerMessage from '../MessageBox/CustomerMessage';
import { errorMsg } from '@/service/serverCode';
import { Message } from '@arco-design/web-react';
import { useLang } from '@/locales/useLang';

interface IMessageListProps {
  getMoreMessage?: () => Promise<IMessage[] | undefined> | undefined;
}

const MessageList: FC<IMessageListProps> = (props) => {
  const userStore = useUserStore();
  const messageStore = useMessageStore();
  const messageList = useMessageStore((state) => state.messageList);
  const scrollMessage = useMessageStore((state) => state.scrollMessage);
  const messageContainerRef = useRef<{ messageContainerRef: HTMLDivElement }>(null);
  const { otherMessage, newMsgCount } = useUserMessage();
  const appStore = useAppStore();
  const [showGetHistoryBtn, setShowGetHistoryBtn] = useState(true);
  const { createSession } = useCreateChat();
  const { seqInfo, getSessionList } = useMessageHistory();
  const { getFaqKnows } = useFaq();
  const $t = useLang();

  const { msgSend } = useChat();

  const { getTimeoutConfig } = useTimeoutConfig();

  useEffect(() => {
    setTimeout(() => {
      const target = messageContainerRef.current?.messageContainerRef;
      target &&
        target.scrollTo({
          top: target.scrollHeight,
          behavior: 'smooth',
        });
    }, 199);
  }, [scrollMessage]);

  /**
   * get & update unread message
   */
  const handleGetUnRead = () => {
    setTimeout(() => {
      let allMsg;
      if (appStore.curDevice === 'phone') {
        allMsg = document.querySelectorAll(
          '#messageContainer .pull-to-refreshify__body > div[class*="you-"]:not(.is-read)'
        );
      } else {
        allMsg = document.querySelectorAll('#messageContainer > div[class*="you-"]:not(.is-read)');
      }
      messageStore.setUnRead(allMsg.length);
    }, 0);
  };

  const showTime = (
    prevTime: number,
    curTime: number,
    sendTime: number,
    isShowFullTime: boolean
  ): string => {
    if (!prevTime) {
      if (isShowFullTime) {
        return formatTime(+sendTime, 'YYYY-MM-DD HH:mm');
      }
      return formatTime(+sendTime, 'HH:mm');
    }
    if (curTime - prevTime > 1000 * 60 * 3) {
      // 3 minutes
      if (isShowFullTime) {
        return formatTime(+sendTime, 'YYYY-MM-DD HH:mm');
      }
      return formatTime(+sendTime, 'HH:mm');
    }

    return '';
  };

  const messageRefs = useRef<HTMLDivElement[]>([]);

  // 监听消息是否在视口内
  const observeMessages = () => {
    const observer = new IntersectionObserver(
      (entries) => {
        // 筛选出见到的消息
        const seeList = entries.filter((item) => item.isIntersecting);

        const youSeeList = seeList.filter((item) => item.target.className.includes('you-'));

        if (!youSeeList.length) {
          return;
        }

        const lastMsg = youSeeList[youSeeList.length - 1];

        const isRead = lastMsg?.target.classList?.contains?.('is-read');
        if (isRead) {
          return;
        }

        const regex = /you-(\d+)/;
        const latestSeq = lastMsg.target.className.match?.(regex)?.[1];

        handleGetUnRead();

        $POST('/miner-api/trans/mark-msg-as-read', {
          seq: latestSeq ? +latestSeq : 0,
          sessionID: userStore.sessionConfig.sessionId,
        });

        lastMsg?.target.classList.add('is-read');
      },
      { threshold: 0.1 }
    ); // 10% 可见时认为是已读

    // 对每个消息元素进行观察
    Object.values(messageRefs.current).forEach((el) => {
      if (el) {
        observer.observe(el);
      }
    });

    return observer;
  };

  useEffect(() => {
    const observer = observeMessages();
    handleGetUnRead();

    return () => {
      observer.disconnect();
    };
  }, [messageStore.messageList]);

  return (
    <>
      <MessageContainer
        ref={messageContainerRef}
        getMoreMessage={() => {
          return props.getMoreMessage?.()?.then((resp) => {
            setShowGetHistoryBtn(!!resp?.length);
            return resp;
          });
        }}
      >
        {appStore.curDevice !== 'phone' &&
          showGetHistoryBtn &&
          messageStore.sessionList?.length > 1 && (
            <GetHistoryButton
              onClick={async () => {
                const resp = await props.getMoreMessage?.();
                setShowGetHistoryBtn(!!resp?.length);
                // const target = messageContainerRef.current?.messageContainerRef;
                // target &&
                //   target.scrollTo({
                //     top: target.scrollHeight,
                //   });
              }}
            />
          )}

        {messageList.map((item, inx, arr) => {
          if (item.msgType === EContentType.CreateSessionContentType) {
            return;
          }
          let isMe = false;
          if (item.senderUid === userStore.sessionConfig.uid) {
            isMe = true;
          }

          if (item.msgType === EContentType.AgentUserJoinSessionContentType) {
            return <CustomerMessage key={item.clientMsgID} msgBody={item.msgBody} />;
          }

          if (item.msgType === EContentType.FaqContentType) {
            return (
              <UserMessage
                ref={(el: any) => {
                  el?.msgRef && (messageRefs.current[inx] = el.msgRef);
                }}
                className={`${'you-' + item.msgSeq} ${item.isRead ? 'is-read' : 'un-read'}`}
                time={showTime(
                  +arr[inx - 1]?.sendTime,
                  +arr[inx]?.sendTime,
                  +item.sendTime,
                  item.msgFromTime === 'history' && !isToday(+item.sendTime)
                )}
                key={item.clientMsgID}
                name={item.senderNickname || $t(robotName)}
                senderFaceURL={item.senderFaceURL}
                defaultAvatar={require('@img/chat/default-robot-avatar.png')}
              >
                <RobotSortOne messageItem={item} msgBody={item.msgBody} />
              </UserMessage>
            );
          }

          if (item.msgType === EContentType.KnowledgePointContentType) {
            return (
              <UserMessage
                ref={(el: any) => {
                  el?.msgRef && (messageRefs.current[inx] = el.msgRef);
                }}
                className={`${'you-' + item.msgSeq} ${item.isRead ? 'is-read' : 'un-read'}`}
                time={showTime(
                  +arr[inx - 1]?.sendTime,
                  +arr[inx]?.sendTime,
                  +item.sendTime,
                  item.msgFromTime === 'history' && !isToday(+item.sendTime)
                )}
                key={item.clientMsgID}
                name={item.senderNickname || $t(robotName)}
                senderFaceURL={item.senderFaceURL}
                defaultAvatar={require('@img/chat/default-robot-avatar.png')}
              >
                <RobotSortTwo messageItem={item} msgBody={item.msgBody} title={item.title} />
              </UserMessage>
            );
          }

          if (item.msgType === EContentType.KnowledgeAnswerContentType) {
            return (
              <UserMessage
                ref={(el: any) => {
                  el?.msgRef && (messageRefs.current[inx] = el.msgRef);
                }}
                className={`${'you-' + item.msgSeq} ${item.isRead ? 'is-read' : 'un-read'}`}
                time={showTime(
                  +arr[inx - 1]?.sendTime,
                  +arr[inx]?.sendTime,
                  +item.sendTime,
                  item.msgFromTime === 'history' && !isToday(+item.sendTime)
                )}
                key={item.clientMsgID}
                name={item.senderNickname || $t(robotName)}
                senderFaceURL={item.senderFaceURL}
                defaultAvatar={require('@img/chat/default-robot-avatar.png')}
              >
                <RobotSortAnswer msgItem={item} />
              </UserMessage>
            );
          }

          if (item.msgType === EContentType.tips) {
            return <TipsWrap key={item.uniqueId}>{item.msgBody}</TipsWrap>;
          }

          if (item.msgType === EContentType.EndSessionContentType) {
            return <TipsWrap key={item.uniqueId}>{$t('chat_session_status_6')}</TipsWrap>;
          }

          if (item.msgType === EContentType.RankingContentType) {
            let rankCount = {
              number: 0,
            };
            try {
              const temp = JSON.parse(item.msgBody);
              rankCount = temp;
            } catch (error) {}

            return (
              <TipsWrap key={item.uniqueId}>
                {$t('chat_session_status_2', { count: rankCount.number })}
              </TipsWrap>
            );
          }

          if (item.msgType === EContentType.TransferContentType) {
            return <TipsWrap key={item.uniqueId}>{$t('chat_session_status_5')}</TipsWrap>;
          }

          if (item.msgType === EContentType.UnsupportedContentType) {
            return <TipsWrap key={item.uniqueId}>{$t('chat_session_status_7')}</TipsWrap>;
          }

          if (item.msgType === EContentType.SystemContentType) {
            return (
              <UserMessage
                ref={(el: any) => {
                  el?.msgRef && (messageRefs.current[inx] = el.msgRef);
                }}
                className={`${'you-' + item.msgSeq} ${item.isRead ? 'is-read' : 'un-read'}`}
                time={showTime(
                  +arr[inx - 1]?.sendTime,
                  +arr[inx]?.sendTime,
                  +item.sendTime,
                  item.msgFromTime === 'history' && !isToday(+item.sendTime)
                )}
                key={item.clientMsgID}
                name={item.senderNickname || $t(robotName)}
                senderFaceURL={item.senderFaceURL}
                defaultAvatar={require('@img/chat/default-robot-avatar.png')}
              >
                <Bubble
                  messageItem={item}
                  status={item.status}
                  msgType={+item.msgType}
                  isMe={false}
                  msgBody={item.msgBody}
                ></Bubble>
              </UserMessage>
            );
          }

          return (
            <UserMessage
              className={`${isMe ? 'self' : 'you-' + item.msgSeq} ${
                item.isRead ? 'is-read' : 'un-read'
              }`}
              isMe={isMe}
              ref={(el: any) => {
                el?.msgRef && (messageRefs.current[inx] = el.msgRef);
              }}
              name={item.senderNickname || $t(customerName)}
              key={item.clientMsgID}
              senderFaceURL={item.senderFaceURL}
              defaultAvatar={
                isMe
                  ? require('@img/chat/icon-default-user.png')
                  : require('@img/chat/icon-default-customer.png')
              }
              time={showTime(
                +arr[inx - 1]?.sendTime,
                +arr[inx]?.sendTime,
                +item.sendTime,
                item.msgFromTime === 'history' && !isToday(+item.sendTime)
              )}
            >
              <Bubble
                messageItem={item}
                onBubbleClick={async (e) => {
                  if (appStore.isKickOff) {
                    Message.info($t('chat_session_status_8'));
                    return;
                  }
                  if (e === 'fail') {
                    const msgList = messageStore.messageList;

                    const id = item.uniqueId;

                    const now = getLocalTime(0).toString();

                    const _targetMsg = msgList.find(
                      (msgItem) => msgItem.uniqueId === item.uniqueId
                    );
                    const targetMsg = clone(_targetMsg, true);

                    targetMsg && (targetMsg.status = EMessageStatus.pending);

                    const newList = msgList.filter((msgItem) => msgItem.uniqueId !== item.uniqueId);
                    targetMsg && messageStore.setMessageList([...newList, targetMsg]);

                    const doSend = async () => {
                      const resp = await msgSend({
                        msgBody: item.msgBody,
                        msgSeq: item.msgSeq,
                        msgType: item.msgType,
                        clientMsgID: id + '-' + now,
                        sendTime: now,
                      }).catch(() => {
                        targetMsg &&
                          (targetMsg.sessionId = userStore.getSessionConfig()?.sessionId);

                        targetMsg && (targetMsg.status = EMessageStatus.fail);
                        const newList = msgList.filter(
                          (msgItem) => msgItem.uniqueId !== item.uniqueId
                        );
                        targetMsg && messageStore.setMessageList([...newList, targetMsg]);
                      });

                      targetMsg && (targetMsg.sessionId = userStore.getSessionConfig()?.sessionId);

                      if (resp?.success && targetMsg) {
                        targetMsg.clientMsgID = id + '-' + now;
                        targetMsg.sendTime = now;
                        targetMsg.uniqueId = id;
                        targetMsg.msgSeq = resp.data?.msgReq!;

                        targetMsg.status = EMessageStatus.success;

                        const newList = msgList.filter(
                          (msgItem) => msgItem.uniqueId !== item.uniqueId
                        );
                        targetMsg && messageStore.setMessageList([...newList, targetMsg]);
                        messageStore.setLatestSeq(resp.data?.msgReq || 0);
                        messageStore.setScrollMessage(msgList);
                      } else if (
                        [
                          errorMsg.sessionError.code,
                          errorMsg.sessionFail.code,
                          errorMsg.sessionTimeout.code,
                        ].includes(resp.code as any)
                      ) {
                        const resp = await createSession();

                        const sessionList = await getSessionList();
                        seqInfo.current.sessionList = sessionList;

                        if (resp.code === 0 && targetMsg) {
                          targetMsg.sessionId = resp.data.sessionId;
                          await doSend();
                          await getFaqKnows(EFaqKnows.knows, '');
                        }
                      } else {
                        targetMsg && (targetMsg.status = EMessageStatus.fail);
                        const newList = msgList.filter(
                          (msgItem) => msgItem.uniqueId !== item.uniqueId
                        );
                        targetMsg && messageStore.setMessageList([...newList, targetMsg]);
                      }
                    };

                    doSend();
                  }
                }}
                status={item.status}
                msgType={+item.msgType}
                isMe={item.senderUid === userStore.sessionConfig.uid}
                msgBody={item.msgBody}
                progress={item.progress}
                isPause={item.isStopUpload}
              ></Bubble>
            </UserMessage>
          );
        })}
      </MessageContainer>
    </>
  );
};

export default MessageList;
